import { reactive, nextTick, ref } from 'vue'
import { supabase } from '../supabaseClient'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'

// ---- 認証トークン
async function getToken() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || ''
}

// ---- JIT署名URL API（submissionId指定）
async function fetchJitUrl(submissionId) {
  const resp = await fetch(`${API_BASE}/api/playback-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    },
    body: JSON.stringify({ submissionId })
  })
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`
    try { 
      const e = await resp.json()
      if (e?.message || e?.error) msg += `: ${e.message || e.error}` 
    } catch {}
    if (resp.status === 429) msg = '発行回数制限です。少し待って再試行してください。'
    if (resp.status === 403) msg = 'アクセス権限がありません。'
    if (resp.status === 404) msg = 'ファイルが見つかりません。'
    throw new Error(msg)
  }
  return resp.json() // { url, expiresAt }
}

// ---- 状態
const players = reactive({}) // id -> { url, expiresAt, refreshing, timer }
const audioRefs = new Map()  // id -> HTMLAudioElement
const loadingId = ref(null)
const prefetching = reactive({}) // id -> Promise (多重発行防止)

// ---- ref セッター
function setAudioRef(id) {
  return (el) => {
    if (el) {
      audioRefs.set(id, el)
    } else {
      audioRefs.delete(id)
    }
  }
}

// ---- 再生開始：JIT発行→プレイヤー表示→再生
async function startPlayback(id) {
  try {
    console.log('🎵 JIT URL取得開始:', id)
    loadingId.value = id
    
    // 既存インスタンスがあればタイマー解除（ビュー切替/再生場所切替対策）
    if (players[id]?.timer) clearInterval(players[id].timer)
    
    const { url, expiresAt } = await fetchJitUrl(id)
    players[id] = { 
      url, 
      expiresAt: Number(expiresAt) || (Date.now() + 5 * 60 * 1000),
      refreshing: false, 
      timer: null,
      lastRefreshAt: 0,
      lockUntil: 0
    }
    
    // DOM更新を待つ
    await nextTick()
    
    const el = audioRefs.get(id)
    if (el) {
      await el.play().catch(() => {})
    }
    
    // 30秒ごとに期限前チェック（90秒マージンで更新）
    players[id].timer = setInterval(() => refreshUrl(id, false), 30_000)
  } catch (e) {
    console.error('JIT URL取得失敗:', e)
    alert(`音声の準備に失敗しました：${e.message}`)
    delete players[id]
  } finally {
    loadingId.value = null
  }
}

// ---- 期限前プリリフレッシュ
const REFRESH_COOLDOWN_MS = 5000     // 最低5秒間隔
const LOCK_ON_429_MS = 20000         // 429時は20秒ロック

async function refreshUrl(id, force = false) {
  const p = players[id]
  if (!p) return
  
  const now = Date.now()
  const nearExpire = p.expiresAt > 0 && (p.expiresAt - now) < 90_000 // 90秒マージン
  
  // 429ロック中は無視
  if (p.lockUntil && now < p.lockUntil) return
  if (!force && !nearExpire) return
  if (p.refreshing) return
  // 直近リフレッシュからの最低間隔（イベント嵐対策）
  if (p.lastRefreshAt && now - p.lastRefreshAt < REFRESH_COOLDOWN_MS) return
  
  p.refreshing = true

  try {
    const el = audioRefs.get(id)
    const wasPlaying = el && !el.paused
    const currentTime = el?.currentTime ?? 0

    console.log('🔄 JIT URL更新:', id)
    const { url, expiresAt } = await fetchJitUrl(id)
    
    // デバッグ: 更新された署名URLを確認
    console.log('🔄 更新後署名URL:', {
      id,
      url: url.substring(0, 100) + '...',
      expiresAt,
      expiresIn: Math.round((Number(expiresAt) - Date.now()) / 1000) + '秒'
    })
    
    p.url = url
    p.expiresAt = Number(expiresAt) || (Date.now() + 5 * 60 * 1000)
    p.lastRefreshAt = Date.now()
    p.lockUntil = 0

    if (el) {
      // シームレス差し替え：読み込み完了で位置復元
      const restore = () => {
        el.removeEventListener('loadedmetadata', restore)
        el.currentTime = Math.max(currentTime - 0.2, 0)
        if (wasPlaying) el.play().catch(() => {})
      }
      el.addEventListener('loadedmetadata', restore, { once: true })
      el.src = url
    }
  } catch (e) {
    // 失敗時もクールダウンを刻む
    p.lastRefreshAt = Date.now()
    // 429（発行回数制限）のときは一定時間ロック
    const msg = String(e?.message || '')
    if (msg.includes('発行回数制限') || msg.includes('429')) {
      p.lockUntil = Date.now() + LOCK_ON_429_MS
    }
    console.error('JIT refresh failed:', e)
  } finally {
    p.refreshing = false
  }
}

// ---- シーク時：期限が30秒未満なら即更新
function maybeSeekRefresh(id) {
  const p = players[id]
  if (!p) return
  if (!p.expiresAt || Number.isNaN(p.expiresAt) || Date.now() > p.expiresAt - 30_000) {
    refreshUrl(id, true)
  }
}

// ---- 停止＆クリーンアップ
function stopPlayback(id) {
  const p = players[id]
  if (p?.timer) {
    clearInterval(p.timer)
  }
  
  const el = audioRefs.get(id)
  if (el) {
    try {
      el.pause()
      el.src = ''
    } catch {}
  }
  
  delete players[id]
  audioRefs.delete(id)
}

// ---- 一時停止（プレイヤーは残す、タイマー継続）
function pausePlayback(id) {
  const el = audioRefs.get(id)
  if (el) {
    try {
      el.pause()
    } catch {}
  }
  
  // タイマーは継続（署名URL有効期限は変わらないため）
  console.log('⏸️ 再生一時停止:', id, '（タイマー継続）')
}

// ---- 再生完了時の処理（プレイヤーは残す、タイマー継続）
function onPlaybackEnded(id) {
  // タイマーは継続（再度再生する可能性があるため）
  console.log('🎵 再生完了:', id, '（タイマー継続）')
}

// ---- プリフェッチ用関数（モーダル開く前にURL準備）
async function prefetchPlaybackUrl(id) {
  // まだ十分な有効期限があるなら再発行しない
  if (players[id]?.url && (players[id].expiresAt - Date.now()) > 90_000) {
    return players[id]
  }
  
  // 多重発行防止
  if (prefetching[id]) return prefetching[id]

  prefetching[id] = (async () => {
    const { url, expiresAt } = await fetchJitUrl(id)
    
    // デバッグ: 署名URLを確認
    console.log('🔗 取得した署名URL:', {
      id,
      url: url.substring(0, 100) + '...',
      expiresAt,
      expiresIn: Math.round((Number(expiresAt) - Date.now()) / 1000) + '秒'
    })
    
    // timer はここでは作らない（再生開始後に付けてもOK）
    players[id] = {
      url,
      expiresAt: Number(expiresAt) || (Date.now() + 5 * 60 * 1000),
      refreshing: false,
      timer: players[id]?.timer ?? null,
      lastRefreshAt: 0,
      lockUntil: 0,
    }
    return players[id]
  })()

  try {
    return await prefetching[id]
  } finally {
    delete prefetching[id]
  }
}

// （必要なら）後からリフレッシュ・タイマーだけ付ける関数
function ensureTimer(id) {
  const p = players[id]
  if (!p || p.timer) return
  p.timer = setInterval(() => refreshUrl(id, false), 30_000)
}

// ---- 全体クリーンアップ（コンポーネントアンマウント時用）
function cleanupAllPlayers() {
  Object.keys(players).forEach((id) => {
    const p = players[id]
    if (p?.timer) clearInterval(p.timer)
    const el = audioRefs.get(id)
    if (el) { 
      try { 
        el.pause() 
        el.src = '' 
      } catch {} 
    }
    audioRefs.delete(id)
    delete players[id]
  })
}

export {
  players,
  loadingId,
  setAudioRef,
  startPlayback,
  refreshUrl,
  maybeSeekRefresh,
  pausePlayback,
  stopPlayback,
  onPlaybackEnded,
  cleanupAllPlayers,
  prefetchPlaybackUrl,
  ensureTimer
}
